import { Inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';

import { MSAL_GUARD_CONFIG, MsalGuardConfiguration, MsalService } from '@azure/msal-angular';
import { RedirectRequest } from '@azure/msal-browser';
import jwt_decode from 'jwt-decode';
import { BehaviorSubject } from 'rxjs';

import { BackendService } from './backend.service';
import { LoginRequest, LoginSsoRequest } from '../models/external/login-request.model';
import { JwtToken } from '../models/jwt-token.model';
import { StaticData } from '../models/static-data.model';
import { UserSession } from '../models/user-session.model';
import {
    decodeSessionFragment,
    isJwtExpired,
    scrubFragment,
    unwrap,
    WGINB_IDTOKEN_KEY,
    WGINB_USER_KEY
} from './sso-fragment.util';

export const InterceptorSkip = 'X-Skip-Interceptor';
export const TokenSessionStorageKey = 'wgteam-token-session'
export const UserSessionStorageKey = 'wgteam-user-session';
export const StaticSessionStorageKey = 'wgteam-static-data';

@Injectable({ providedIn: 'root' })
export class AuthService {
    isUserLoggedIn$ = new BehaviorSubject<boolean>(false);

    sessionItems = [
        TokenSessionStorageKey,
        UserSessionStorageKey, 
        StaticSessionStorageKey
    ];

    constructor(
        private router: Router,
        private backendService: BackendService,
        @Inject(MSAL_GUARD_CONFIG) private msalGuardConfig: MsalGuardConfiguration, 
        private msalService: MsalService
    ) {
        this.isUserLoggedIn$.next(this.isLoggedIn());
    }

    login(loginData: LoginRequest) {
        return this.backendService.login(loginData);
    }

    loginSso() {
        if (this.msalGuardConfig.authRequest) {
            this.msalService.loginRedirect({ ...this.msalGuardConfig.authRequest } as RedirectRequest);
        } else {
            this.msalService.loginRedirect();
        }
    }

    loginUsingSso(loginData: LoginSsoRequest) {
        return this.backendService.loginUsingSso(loginData);
    }

    /**
     * SSO Apps Portal handoff: if the URL carries a `#session=` fragment, hydrate the session
     * (token + user + static) directly from it so the user lands logged in without re-clicking SSO.
     * Runs as an APP_INITIALIZER, before the auth guard. Resolves false (and falls through to the
     * normal login screen) when there is no fragment, it is malformed, or the token is expired.
     */
    hydrateFromFragment(): Promise<boolean> {
        const session = decodeSessionFragment();
        if (!session) {
            return Promise.resolve(false);
        }

        const idtoken = unwrap(session[WGINB_IDTOKEN_KEY]);
        const user = unwrap(session[WGINB_USER_KEY]);

        if (typeof idtoken !== 'string' || !user || isJwtExpired(idtoken)) {
            scrubFragment();
            return Promise.resolve(false);
        }

        return new Promise<boolean>((resolve) => {
            this.backendService.storeSessionAndFetchStatic(idtoken, user).subscribe({
                next: () => {
                    scrubFragment();
                    this.isUserLoggedIn$.next(true);
                    resolve(true);
                },
                error: () => {
                    scrubFragment();
                    resolve(false);
                }
            });
        });
    }

    logout(): void {
        // Explicitly remove MSAL tokens first
        sessionStorage.removeItem('msal-access-token');
        sessionStorage.removeItem('msal-id-token');
        
        // Clear auth-related session storage items
        this.sessionItems.forEach(key => sessionStorage.removeItem(key));
        
        // Clear any remaining auth-related items
        sessionStorage.removeItem('isAuthLoading');
        
        this.isUserLoggedIn$.next(false);
        this.router.navigate(['/login']);
    }

    getUserSessionData(value: keyof UserSession): string {
        const sessionData = sessionStorage.getItem(UserSessionStorageKey);
        if (!sessionData) {
            throw new Error('Session data not found');
        }
        
        try {
            const session: UserSession = JSON.parse(sessionData);
            return session[value] || '';
        } catch (error) {
            console.error('Failed to parse session data:', error);
            throw new Error('Invalid session data');
        }
    }

    getToken(): string | null {
        return sessionStorage.getItem(TokenSessionStorageKey);
    }

    getTokenData(value: keyof JwtToken): string {
        const jwt = this.getToken();
        if (!jwt) {
            throw new Error('Token not found');
        }
        const decodedJwt: any = jwt_decode(jwt);
        return decodedJwt[value];
    }

    getStaticData(value: keyof StaticData): any {
        const staticData = sessionStorage.getItem(StaticSessionStorageKey);
        if (!staticData) {
            throw new Error('Static data not found');
        }
        
        try {
            const staticDataObject: StaticData = JSON.parse(staticData);
            return staticDataObject[value] || '';
        } catch (error) {
            console.error('Failed to parse static data:', error);
            throw new Error('Invalid static data');
        }
    }

    checkPermission(permissionName: string): boolean {
        try {
            const permissionList = this.getStaticData('UI_PERMS');
            if (!Array.isArray(permissionList) || permissionList.length === 0) {
                return false;
            }
            return permissionList.includes(permissionName);
        } catch {
            return false;
        }
    }

    isLoggedIn(): boolean {
        const sessionData = sessionStorage.getItem(UserSessionStorageKey);
        if (!sessionData) {
            return false;
        }
        
        try {
            const session: UserSession = JSON.parse(sessionData);
            if (!session) {
                return false;
            }
            
            const jwt = this.getToken();
            if (!jwt) {
                return false;
            }
            
            const expiry: any = +this.getTokenData('exp');
            const timeExpired = new Date().getTime() > expiry * 1000;
            
            return !timeExpired;
        } catch (error) {
            return false;
        }
    }

    isMsalLoggedIn(): boolean {
        // FIX: Read MSAL tokens from sessionStorage instead of cookies
        // This prevents RequestHeaderSectionTooLarge error on hard refresh
        let msalAccessToken = sessionStorage.getItem('msal-access-token');
        let msalIdToken = sessionStorage.getItem('msal-id-token');

        if (msalAccessToken && msalIdToken) {
            try {
              const tokenData = JSON.parse(msalAccessToken);
              if (tokenData.expiry) {
                const expiryTime = new Date(tokenData.expiry).getTime();
                const now = new Date().getTime();
                return expiryTime > now;
              }
              return false;
            }
            catch (error) {
              return false;
            } 
        }
        return false;
    }
}
